import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "store";
import { setSwitchTimeEnabled } from "store/slices/tradingSlice";
import websocketService from "services/websocket";
import { formatClockTime, getExpiryTimestamp } from "shared/utils";
import { getStoredSwitchTimeEnabled, setStoredSwitchTimeEnabled } from "./timeSessionStorage";

const TICK_MS = 1000;

const getExpiryClockTime = (orderDuration: number) =>
  formatClockTime(
    getExpiryTimestamp(websocketService.getCurrentServerTime(), orderDuration, true),
  );

/**
 * Тумблер режима Switch Time на поле Time. По умолчанию выключен —
 * даже если пользователю доступна кнопка (status.switch_time === true).
 * Пока включён, поле Time и линия экспирации на графике показывают часы
 * и минуты истечения ордера. Состояние живёт в redux, чтобы Chart.tsx
 * рисовал линию в том же режиме.
 */
export const useSwitchTime = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const enabled = useSelector((state: RootState) => state.trading.switchTimeEnabled);
  const orderDuration = useSelector((state: RootState) => state.trading.orderDuration);
  const [expiryClockTime, setExpiryClockTime] = useState(() => getExpiryClockTime(orderDuration));

  // Последний выбор режима переживает перезагрузку страницы — см. timeSessionStorage.
  useEffect(() => {
    if (userId == null) return;
    const stored = getStoredSwitchTimeEnabled(userId);
    if (stored != null) dispatch(setSwitchTimeEnabled(stored));
  }, [userId, dispatch]);

  const toggle = useCallback(() => {
    const next = !enabled;
    dispatch(setSwitchTimeEnabled(next));
    if (userId != null) setStoredSwitchTimeEnabled(userId, next);
  }, [dispatch, enabled, userId]);

  useEffect(() => {
    if (!enabled) return;

    const update = () => setExpiryClockTime(getExpiryClockTime(orderDuration));
    update();
    const id = setInterval(update, TICK_MS);
    return () => clearInterval(id);
  }, [enabled, orderDuration]);

  return { enabled, toggle, expiryClockTime };
};
